import { createApp, createSSRApp, h } from 'vue'
import { createInertiaApp, router } from '@inertiajs/vue3'
import CookieConsent from '../components/CookieConsent.vue'
import { initialPageFromDocument, resolvePage } from '../lib/inertiaPages'
import { installAnalytics } from '../lib/analytics'
import { installPrivacyNavigationBoundary } from '../lib/privacyNavigation'
import {
  assetEntrySignature,
  availableSessionStorage,
  claimAssetRecoveryAttempt,
  planAssetRecovery,
  requestManualAssetReload,
} from '../lib/portalAssetRecovery'
import '../lib/sameDocumentHistoryGuard'
import { installPortalDiagnostics } from '../lib/portalDiagnostics'
import mariaAssistantAvatarUrl from '../../assets/images/rooted-movement-maria-assistant-avatar.jpg?url'
import { createAssistantDiscoveryCall } from '../lib/assistantDiscoveryCall'
import assistantDiscoveryCallStyles from '../styles/assistant-discovery-call.css?inline'
import assistantPanelStyles from '../styles/assistant-panel.css?inline'

import '@fontsource/dm-sans/400.css'
import '@fontsource/dm-sans/500.css'
import '@fontsource/dm-sans/600.css'
import '@fontsource/dm-sans/700.css'
import '@fontsource/space-mono/400.css'
import '@fontsource/space-mono/700.css'
import '../styles/colors.css'
import '../styles/base.css'
import '../styles/design-system.css'
import '../styles/auth.css'
import '../styles/site-header.css'
import '../styles/site-pages.css'
import '../styles/bali-program.css'
import '../styles/portal.css'
import '../styles/portal-community.css'
import '../styles/admin.css'
import '../styles/cookie-consent.css'
import '../styles/admin-community-moderation.css'
import '../styles/admin-community.css'
import '../styles/community-circles.css'

const mountElement = document.getElementById('app')

// Installed before the app boots so a failure during startup is still captured
// for "Report a problem". Private app surfaces only; this never runs publicly.
if (window.location.pathname.startsWith('/portal')) installPortalDiagnostics()
const assetRecoveryNoticeId = 'rooted-asset-recovery-notice'

const showAssetRecoveryNotice = () => {
  if (document.getElementById(assetRecoveryNoticeId)) return

  const notice = document.createElement('section')
  notice.id = assetRecoveryNoticeId
  notice.className = 'portal-asset-recovery'
  notice.setAttribute('role', 'alert')
  notice.setAttribute('aria-live', 'assertive')

  const title = document.createElement('strong')
  title.textContent = 'The app needs a manual refresh'

  const message = document.createElement('span')
  message.textContent = 'Automatic refreshing has stopped to keep this page stable. Update when you are ready.'

  const button = document.createElement('button')
  button.type = 'button'
  button.textContent = 'Update app'
  button.addEventListener('click', () => {
    requestManualAssetReload(
      availableSessionStorage(window),
      () => window.location.reload(),
    )
  })

  notice.append(title, message, button)
  ;(document.body || document.documentElement).append(notice)
}

// A restored tab can retain an old entry bundle after a deploy while its lazily
// loaded page chunk has already been removed. Attempt one automatic recovery for
// that entry revision, then stop and let the visitor choose when to try again.
window.addEventListener('vite:preloadError', event => {
  event.preventDefault()

  const entrySignature = assetEntrySignature(document) || window.location.pathname
  const recovery = claimAssetRecoveryAttempt(
    entrySignature,
    availableSessionStorage(window),
    'preload',
  )

  if (recovery === 'reload') window.location.reload()
  else showAssetRecoveryNotice()
})

const isAppPath = path => path.startsWith('/portal') || path.startsWith('/admin') || path.startsWith('/partner')
const isPortalDiscoveryPath = path => path === '/portal' || path.startsWith('/portal/')
const isAuthPath = path => path === '/sign_in' || path === '/password' || path.startsWith('/password/')
const isVerificationPath = path => path.startsWith('/verify/')
const isBookingBreezePreviewPath = path => path === '/bookingbreeze-preview'
const isPrivatePurchasePath = path => path.startsWith('/online-courses/purchases/')
const isCourseCheckoutPath = path => /^\/online-courses\/[^/]+$/.test(path)

const isStandaloneApp = () => (
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true
) && isAppPath(window.location.pathname)

const installCookieConsent = initialPage => {
  if (initialPage?.props?.cookieConsentVisible !== true) return
  if (document.getElementById('rooted-cookie-consent')) return

  const host = document.createElement('div')
  host.id = 'rooted-cookie-consent'
  document.body.appendChild(host)

  createApp(CookieConsent, {
    initialConsent: initialPage.props.cookieConsent,
    csrfToken: initialPage.props.csrfToken,
  }).mount(host)
}

const installRootedAssistant = ({ discoveryOnly = false } = {}) => {
  let disposed = false
  const bindings = []
  const listen = (target, name, callback, options) => {
    target.addEventListener(name, callback, options)
    bindings.push([target, name, callback, options])
  }
  if (
    (isAppPath(window.location.pathname) && !(discoveryOnly && isPortalDiscoveryPath(window.location.pathname))) ||
    isAuthPath(window.location.pathname) ||
    isVerificationPath(window.location.pathname) ||
    isBookingBreezePreviewPath(window.location.pathname) ||
    isPrivatePurchasePath(window.location.pathname) ||
    isCourseCheckoutPath(window.location.pathname)
  ) return
  if (document.getElementById('rooted-assistant-shadow-host')) return

  const host = document.createElement('div')
  host.id = 'rooted-assistant-shadow-host'
  host.toggleAttribute('data-discovery-only', discoveryOnly)

  const syncAssistantRouteState = () => {
    const isUpcomingCohorts = window.location.pathname === '/upcoming-cohorts'
    const delayMobileLauncher = isUpcomingCohorts && window.innerWidth <= 767

    host.toggleAttribute('data-hide-promo-compact', window.location.pathname === '/program')
    host.toggleAttribute('data-delay-launcher-mobile', delayMobileLauncher)
    host.toggleAttribute('data-mobile-scrolled', !delayMobileLauncher || window.scrollY > 8)
  }

  syncAssistantRouteState()
  listen(document, 'inertia:navigate', syncAssistantRouteState)
  listen(window, 'popstate', syncAssistantRouteState)
  listen(window, 'resize', syncAssistantRouteState)
  listen(window, 'scroll', syncAssistantRouteState, { passive: true })
  document.body.appendChild(host)

  const mariaWhatsAppNumber = '6281237600771'
  const shadow = host.attachShadow({ mode: 'open' })

  shadow.innerHTML = `
    <style>
      :host {
        --assistant-bg: var(--bg-main);
        --assistant-surface: var(--bg-surface);
        --assistant-text: var(--text-main);
        --assistant-muted: var(--text-muted);
        --assistant-accent: var(--accent-primary);
        --assistant-surface-accent: var(--bg-surface);
        --assistant-shadow: 0 1.1rem 3rem color-mix(in oklch, var(--assistant-text) 23%, transparent);
        font-family: var(--font-sans, Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
      }

      *, *::before, *::after {
        box-sizing: border-box;
      }

      button,
      textarea,
      a {
        font: inherit;
      }

      .rooted-assistant {
        position: fixed;
        right: 24px;
        bottom: 24px;
        z-index: 150;
        display: grid;
        justify-items: end;
        gap: 0.65rem;
        color: var(--assistant-text);
      }

      .rooted-assistant__promo {
        display: flex;
        min-height: 3.48rem;
        align-items: center;
        padding: 0.95rem 1.35rem;
        border: 0;
        border-radius: 8px;
        background: var(--assistant-bg);
        box-shadow: 0 0.65rem 1.8rem color-mix(in oklch, var(--assistant-text) 16%, transparent);
        color: var(--assistant-text);
        cursor: pointer;
        font-size: 0.916rem;
        font-weight: 500;
        line-height: 1.2;
        transition: opacity 180ms ease, transform 180ms ease;
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__promo {
        opacity: 0;
        transform: translateY(0.4rem) scale(0.96);
        pointer-events: none;
      }

      .rooted-assistant__launcher {
        display: inline-grid;
        width: 3.664rem;
        height: 3.664rem;
        place-items: center;
        padding: 0;
        border: 1px solid color-mix(in oklch, var(--assistant-surface-accent) 44%, var(--assistant-text));
        border-radius: 12px;
        background: linear-gradient(145deg, color-mix(in oklch, var(--assistant-text) 86%, var(--assistant-surface-accent)), var(--assistant-text));
        box-shadow: var(--assistant-shadow);
        color: var(--assistant-bg);
        cursor: pointer;
        transition: transform 180ms ease, background-color 180ms ease;
      }

      .rooted-assistant__launcher svg {
        width: 1.7rem;
        height: 1.7rem;
      }

      .rooted-assistant__launcher-close {
        display: none;
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__launcher-chat {
        display: none;
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__launcher-close {
        display: block;
      }

      .rooted-assistant__promo:hover,
      .rooted-assistant__promo:focus-visible,
      .rooted-assistant__launcher:hover,
      .rooted-assistant__launcher:focus-visible {
        outline: none;
        transform: translateY(-2px);
      }

      .rooted-assistant__promo:focus-visible,
      .rooted-assistant__launcher:focus-visible {
        box-shadow: 0 0 0 3px color-mix(in oklch, var(--assistant-bg) 72%, transparent), var(--assistant-shadow);
      }

      .rooted-assistant__panel {
        position: absolute;
        right: 0;
        bottom: calc(3.664rem + 0.85rem);
        display: flex;
        width: min(24rem, calc(100vw - 2rem));
        max-height: min(39rem, calc(100dvh - 7rem));
        flex-direction: column;
        overflow: hidden;
        border: 1px solid color-mix(in oklch, var(--assistant-text) 9%, transparent);
        border-radius: 12px;
        background: var(--assistant-bg);
        box-shadow: var(--assistant-shadow);
        opacity: 0;
        transform: translateY(0.7rem) scale(0.975);
        transform-origin: right bottom;
        pointer-events: none;
        transition: opacity 180ms ease, transform 180ms ease;
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__panel {
        opacity: 1;
        transform: translateY(0) scale(1);
        pointer-events: auto;
      }

      .rooted-assistant__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        padding: 1rem;
        background: var(--assistant-surface-accent);
        color: var(--assistant-text);
      }

      .rooted-assistant__identity {
        display: flex;
        align-items: center;
        gap: 0.7rem;
      }

      .rooted-assistant__avatar {
        display: block;
        width: 2.65rem;
        height: 2.65rem;
        flex: 0 0 auto;
        overflow: hidden;
        border: 2px solid color-mix(in oklch, var(--assistant-bg) 88%, transparent);
        border-radius: 20%;
        background: var(--assistant-bg);
      }

      .rooted-assistant__avatar img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        scale: 1.12;
        translate: 0 -5%;
      }

      .rooted-assistant__eyebrow,
      .rooted-assistant__title {
        display: block;
      }

      .rooted-assistant__eyebrow {
        color: color-mix(in oklch, var(--assistant-text) 70%, transparent);
        font-size: 0.68rem;
        letter-spacing: 0.08em;
        line-height: 1.2;
        text-transform: uppercase;
      }

      .rooted-assistant__title {
        margin-top: 0.12rem;
        font-size: 1rem;
        font-weight: 650;
        line-height: 1.2;
      }

      .rooted-assistant__header-close {
        display: grid;
        width: 2rem;
        height: 2rem;
        place-items: center;
        padding: 0;
        border: 0;
        border-radius: 999rem;
        background: color-mix(in oklch, var(--assistant-text) 12%, transparent);
        color: var(--assistant-text);
        cursor: pointer;
        font-size: 1.2rem;
        line-height: 1;
      }

      .rooted-assistant__conversation {
        display: flex;
        min-height: 28rem;
        flex-direction: column;
        gap: 0.85rem;
        padding: 1rem;
        overflow-y: auto;
        background: color-mix(in oklch, var(--assistant-surface-accent) 9%, var(--assistant-bg));
      }

      .rooted-assistant__message {
        max-width: 88%;
        margin: 0;
        padding: 0.78rem 0.88rem;
        border-radius: 0.2rem 0.85rem 0.85rem 0.85rem;
        background: var(--assistant-bg);
        box-shadow: 0 0.22rem 0.75rem color-mix(in oklch, var(--assistant-text) 8%, transparent);
        color: var(--assistant-text);
        font-size: 0.9rem;
        line-height: 1.42;
      }

      .rooted-assistant__timestamp {
        margin: 0.35rem 0 0 0.2rem;
        color: color-mix(in oklch, var(--assistant-muted) 72%, transparent);
        font-size: 0.67rem;
        line-height: 1.2;
      }

      .rooted-assistant__choices {
        display: grid;
        gap: 0.5rem;
        margin-top: auto;
      }

      .rooted-assistant__choice {
        display: flex;
        min-height: 2.8rem;
        align-items: center;
        justify-content: space-between;
        gap: 0.8rem;
        padding: 0.72rem 0.82rem;
        border: 1px solid color-mix(in oklch, var(--assistant-surface-accent) 34%, transparent);
        border-radius: 0.72rem;
        background: color-mix(in oklch, var(--assistant-surface-accent) 8%, var(--assistant-bg));
        color: var(--assistant-text);
        cursor: pointer;
        font-size: 0.84rem;
        font-weight: 620;
        line-height: 1.2;
        text-align: left;
        text-decoration: none;
        transition: border-color 150ms ease, transform 150ms ease, background-color 150ms ease;
      }

      .rooted-assistant__choice::after {
        content: "→";
        flex: 0 0 auto;
        color: var(--assistant-accent);
      }

      .rooted-assistant__choice:hover,
      .rooted-assistant__choice:focus-visible {
        border-color: color-mix(in oklch, var(--assistant-accent) 55%, transparent);
        background: color-mix(in oklch, var(--assistant-surface-accent) 18%, var(--assistant-bg));
        outline: none;
        transform: translateY(-1px);
      }

      .rooted-assistant__composer {
        display: none;
        flex-direction: column;
        gap: 0.75rem;
      }

      .rooted-assistant[data-view="whatsapp"] .rooted-assistant__home {
        display: none;
      }

      .rooted-assistant[data-view="whatsapp"] .rooted-assistant__composer {
        display: flex;
      }

      .rooted-assistant__composer > .rooted-assistant__eyebrow {
        color: var(--assistant-muted);
      }

      .rooted-assistant__textarea {
        width: 100%;
        min-height: 8.5rem;
        resize: vertical;
        padding: 0.82rem;
        border: 1px solid color-mix(in oklch, var(--assistant-text) 18%, transparent);
        border-radius: 0.72rem;
        background: var(--assistant-bg);
        color: var(--assistant-text);
        font-size: 0.9rem;
        line-height: 1.42;
      }

      .rooted-assistant__textarea:focus {
        border-color: var(--assistant-accent);
        outline: 2px solid color-mix(in oklch, var(--assistant-accent) 18%, transparent);
        outline-offset: 1px;
      }

      .rooted-assistant__composer-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.65rem;
      }

      .rooted-assistant__back,
      .rooted-assistant__send {
        min-height: 2.7rem;
        padding: 0.68rem 0.9rem;
        border: 0;
        border-radius: 999rem;
        cursor: pointer;
        font-size: 0.8rem;
        font-weight: 650;
      }

      .rooted-assistant__back {
        background: transparent;
        color: var(--assistant-muted);
      }

      .rooted-assistant__send {
        background: var(--assistant-text);
        color: var(--assistant-bg);
      }

      .rooted-assistant__note {
        margin: 0;
        color: var(--assistant-muted);
        font-size: 0.7rem;
        line-height: 1.35;
      }

      @media (max-width: 767px) {
        .rooted-assistant {
          right: 1rem;
          bottom: 1.25rem;
        }

        .rooted-assistant__promo {
          display: none;
        }

        .rooted-assistant__panel {
          width: calc(100vw - 2rem);
          max-height: calc(100dvh - 6.5rem);
        }

        .rooted-assistant__conversation {
          min-height: min(28rem, calc(100dvh - 13rem));
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .rooted-assistant__promo,
        .rooted-assistant__launcher,
        .rooted-assistant__panel,
        .rooted-assistant__choice {
          transition: none;
        }
      }

      /* Lorikeet-inspired geometry, adapted to Rooted's content and brand tokens. */
      :host {
        --assistant-bg: var(--assistant-canvas);
        --assistant-header-bg: var(--assistant-header);
        --assistant-surface: var(--assistant-panel-surface);
        --assistant-border-color: var(--assistant-border);
        --assistant-text: var(--assistant-copy);
        --assistant-muted: var(--assistant-copy-muted);
        --assistant-accent: var(--assistant-highlight);
        --assistant-shadow: 0 0 8px color-mix(in oklch, var(--color-black) 20%, transparent);
      }

      .rooted-assistant {
        right: 24px;
        bottom: 24px;
        display: block;
        width: 4rem;
        height: 4rem;
        color: var(--assistant-text);
      }

      .rooted-assistant__promo {
        position: absolute;
        right: calc(100% + 0.75rem);
        bottom: 0;
        min-height: 4rem;
        padding: 1.1rem 1.5rem;
        border: 1px solid transparent;
        border-radius: 1rem;
        background-color: color-mix(in oklch, var(--assistant-bg) 75%, transparent);
        background-image:
          linear-gradient(color-mix(in oklch, var(--assistant-header-bg) 45%, transparent), color-mix(in oklch, var(--assistant-header-bg) 45%, transparent)),
          linear-gradient(135deg, color-mix(in oklch, var(--assistant-text) 35%, transparent), color-mix(in oklch, var(--assistant-text) 5%, transparent));
        background-origin: border-box;
        background-clip: padding-box, border-box;
        box-shadow: 0 4px 24px color-mix(in oklch, var(--color-black) 30%, transparent);
        backdrop-filter: blur(5px);
        color: var(--assistant-text);
        font-size: 1.125rem;
        font-weight: 400;
        line-height: 1.4;
        white-space: nowrap;
        transition: opacity 350ms ease, transform 350ms ease;
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__promo {
        opacity: 0;
        transform: translateX(10px) scale(0.95);
      }

      @media (min-width: 1025px) {
        .rooted-assistant[data-promo-visible="false"] .rooted-assistant__promo {
          visibility: hidden;
          opacity: 0;
          transform: translateX(10px) scale(0.95);
          pointer-events: none;
        }
      }

      .rooted-assistant__launcher {
        width: 4rem;
        height: 4rem;
        padding: 10px;
        border: 0;
        border-radius: 12px;
        background: var(--assistant-header-bg);
        box-shadow: var(--assistant-shadow);
        color: var(--assistant-text);
        transition:
          opacity 250ms ease-in-out,
          transform 250ms ease-in-out,
          visibility 0s linear 0s;
      }

      .rooted-assistant__launcher:hover,
      .rooted-assistant__launcher:focus-visible {
        transform: scale(1.05);
      }

      .rooted-assistant[data-open="true"] .rooted-assistant__launcher {
        visibility: hidden;
        opacity: 0;
        transform: scale(0.88);
        pointer-events: none;
      }

      .rooted-assistant__panel {
        --assistant-bg: var(--color-white, oklch(1 0 0));
        --assistant-header-bg: var(--color-white, oklch(1 0 0));
        --assistant-surface: color-mix(in oklch, var(--color-black, oklch(0 0 0)) 4%, var(--color-white, oklch(1 0 0)));
        --assistant-border-color: color-mix(in oklch, var(--color-black, oklch(0 0 0)) 12%, transparent);
        --assistant-text: var(--text-main);
        --assistant-muted: var(--text-muted);
        --assistant-accent: var(--accent-primary);

        position: fixed;
        right: 20px;
        bottom: 20px;
        width: min(22rem, calc(100vw - 2.5rem));
        height: min(28rem, calc(100dvh - 6.5rem));
        max-height: none;
        box-sizing: content-box;
        border: 1px solid var(--assistant-border-color);
        border-radius: 0.5rem;
        background: var(--assistant-bg);
        box-shadow: var(--assistant-shadow);
        transform: translateY(20px) scale(0.88);
        transform-origin: right bottom;
        transition: opacity 200ms cubic-bezier(0.25, 1.25, 0.5, 1), transform 200ms cubic-bezier(0.25, 1.25, 0.5, 1);
      }

      .rooted-assistant__panel:focus {
        outline: none;
      }

      .rooted-assistant__header {
        min-height: 4.0625rem;
        flex: 0 0 auto;
        padding: 0.75rem 1rem;
        background: var(--assistant-header-bg);
        color: var(--assistant-text);
      }

      .rooted-assistant__identity {
        gap: 0.75rem;
      }

      .rooted-assistant__avatar {
        width: 2rem;
        height: 2rem;
        border: 1px solid color-mix(in oklch, var(--assistant-text) 45%, transparent);
        background: var(--assistant-surface);
      }

      .rooted-assistant__eyebrow {
        color: var(--assistant-muted);
        font-size: 0.625rem;
      }

      .rooted-assistant__title {
        margin-top: 0;
        color: var(--assistant-text);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.25rem;
      }

      .rooted-assistant__header-close {
        width: 2.5rem;
        height: 2.5rem;
        flex-shrink: 0;
        padding: 0;
        border-radius: 0.25rem;
        background: transparent;
        color: var(--assistant-muted);
      }

      .rooted-assistant__header-close svg {
        display: block;
        width: 0.75rem;
        height: 0.75rem;
      }

      .rooted-assistant__header-close:hover,
      .rooted-assistant__header-close:focus-visible {
        background: var(--assistant-surface);
        color: var(--assistant-text);
        outline: none;
      }

      .rooted-assistant__conversation {
        min-height: 0;
        flex: 1 1 auto;
        gap: 0.5rem;
        padding: 1rem;
        background: var(--assistant-bg);
      }

      .rooted-assistant__message {
        max-width: calc(100% - 2.75rem);
        margin-left: 2.75rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.75rem;
        background: var(--assistant-surface);
        box-shadow: none;
        color: var(--assistant-text);
        font-size: 0.875rem;
        line-height: 1.25rem;
      }

      .rooted-assistant__timestamp {
        margin: 0.25rem 0 0 2.75rem;
        color: var(--assistant-muted);
        font-size: 0.75rem;
        line-height: 0.875rem;
      }

      .rooted-assistant__choices {
        gap: 0.5rem;
      }

      .rooted-assistant__choice {
        min-height: 2.75rem;
        padding: 0.625rem 0.75rem;
        border: 1px solid var(--assistant-border-color);
        border-radius: 0.5rem;
        background: var(--assistant-header-bg);
        color: var(--assistant-text);
        font-size: 0.875rem;
        font-weight: 500;
        line-height: 1.25rem;
      }

      .rooted-assistant__choice::after {
        color: var(--accent-primary-text);
      }

      .rooted-assistant__choice:hover,
      .rooted-assistant__choice:focus-visible {
        border-color: var(--assistant-accent);
        background: var(--assistant-surface);
      }

      .rooted-assistant__composer > .rooted-assistant__eyebrow,
      .rooted-assistant__note,
      .rooted-assistant__back {
        color: var(--assistant-muted);
      }

      .rooted-assistant__textarea {
        border-color: var(--assistant-border-color);
        border-radius: 0.5rem;
        background: var(--assistant-header-bg);
        color: var(--assistant-text);
      }

      .rooted-assistant__textarea::placeholder {
        color: var(--assistant-muted);
      }

      .rooted-assistant__send {
        border-radius: 0.5rem;
        background: var(--assistant-text);
        color: var(--assistant-bg);
      }

      .rooted-assistant__send:hover,
      .rooted-assistant__send:focus-visible {
        background: color-mix(in oklch, var(--assistant-text) 88%, var(--color-black, oklch(0 0 0)));
        outline: 2px solid color-mix(in oklch, var(--assistant-text) 24%, transparent);
        outline-offset: 2px;
      }

      @media (max-width: 767px) {
        .rooted-assistant__promo {
          display: none;
        }
      }

      .rooted-assistant[data-homepage="true"] .rooted-assistant__promo {
        display: flex;
      }

      @media (max-width: 600px) {
        .rooted-assistant {
          right: 1.25rem;
          bottom: 1.25rem;
          width: 3.5rem;
          height: 3.5rem;
        }

        .rooted-assistant__promo {
          right: 0;
          bottom: calc(100% + 0.75rem);
          display: flex;
          min-height: 4rem;
          padding: 0.875rem 1rem;
          border-radius: 0.75rem;
          font-size: 0.875rem;
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
          transform: translateY(0.75rem) scale(0.96);
          transition:
            opacity 360ms ease,
            transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1),
            visibility 0s linear 420ms;
        }

        .rooted-assistant[data-promo-visible="true"] .rooted-assistant__promo {
          visibility: visible;
          opacity: 1;
          pointer-events: auto;
          transform: translateY(0) scale(1);
          transition-delay: 0s;
        }

        .rooted-assistant[data-open="true"] .rooted-assistant__promo {
          visibility: hidden;
          opacity: 0;
          pointer-events: none;
          transform: translateY(0.75rem) scale(0.96);
        }

        .rooted-assistant__launcher {
          width: 3.5rem;
          height: 3.5rem;
        }

        .rooted-assistant__panel {
          right: 1rem;
          bottom: 1rem;
          left: 1rem;
          width: auto;
          height: min(28rem, calc(100dvh - 2rem));
          max-height: none;
          box-sizing: border-box;
          border-radius: 0.75rem;
          transform-origin: center bottom;
        }

        .rooted-assistant__conversation {
          min-height: 0;
        }
      }

      @media (max-width: 767px) {
        :host([data-delay-launcher-mobile]:not([data-mobile-scrolled])) .rooted-assistant__promo,
        :host([data-delay-launcher-mobile]:not([data-mobile-scrolled])) .rooted-assistant__launcher {
          visibility: hidden;
          opacity: 0;
          transform: translateY(0.75rem) scale(0.9);
          pointer-events: none;
        }
      }

      @media (max-width: 1024px) {
        :host([data-hide-promo-compact]) .rooted-assistant__promo {
          display: none;
        }
      }

      .rooted-assistant[data-footer-overlap="true"] .rooted-assistant__promo,
      .rooted-assistant[data-footer-overlap="true"] .rooted-assistant__launcher {
        visibility: hidden;
        opacity: 0;
        pointer-events: none;
        transition:
          opacity 250ms ease,
          transform 250ms ease,
          visibility 0s linear 250ms;
      }

      .rooted-assistant[data-footer-overlap="true"] .rooted-assistant__promo {
        transform: translateY(0.75rem) scale(0.96);
      }

      .rooted-assistant[data-footer-overlap="true"] .rooted-assistant__launcher {
        transform: translateY(0.75rem) scale(0.9);
      }

      .rooted-assistant[data-journey-overlap="true"] .rooted-assistant__promo,
      .rooted-assistant[data-journey-overlap="true"] .rooted-assistant__launcher,
      .rooted-assistant[data-promo-hidden="true"] .rooted-assistant__promo,
      .rooted-assistant[hidden] {
        display: none;
      }

      @media (prefers-reduced-motion: reduce) {
        .rooted-assistant__promo,
        .rooted-assistant__launcher,
        .rooted-assistant__panel,
        .rooted-assistant__choice {
          transition: none;
        }
      }

      :host([data-discovery-only]) .rooted-assistant {
        z-index: var(--z-overlay);
      }

      :host([data-discovery-only]) .rooted-assistant__promo,
      :host([data-discovery-only]) .rooted-assistant__launcher,
      :host([data-discovery-only]) .rooted-assistant__home,
      :host([data-discovery-only]) .rooted-assistant__composer {
        display: none;
      }

      ${assistantPanelStyles}
      ${assistantDiscoveryCallStyles}
    </style>

    <div class="rooted-assistant" data-open="false" data-view="home" data-promo-visible="false" data-promo-hidden="false" data-footer-overlap="false" data-journey-overlap="false">
      <button class="rooted-assistant__promo" type="button">Hey there! Got any questions?</button>

      <section class="rooted-assistant__panel" role="dialog" aria-label="Rooted Movement assistant" aria-hidden="true" tabindex="-1" inert>
        <header class="rooted-assistant__header">
          <div class="rooted-assistant__identity">
            <span class="rooted-assistant__avatar" aria-hidden="true">
              <img src="${mariaAssistantAvatarUrl}" alt="">
            </span>
            <span>
              <strong class="rooted-assistant__title">Let’s talk</strong>
            </span>
          </div>
          <button class="rooted-assistant__header-close" type="button" aria-label="Close assistant">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" aria-hidden="true" focusable="false">
              <path d="m5 5 14 14M19 5 5 19" />
            </svg>
          </button>
        </header>

        <div class="rooted-assistant__conversation">
          <div class="rooted-assistant__home">
            <p class="rooted-assistant__message">Hi, I’m here to help with the Rooted Movement Pilates teacher training. What would you like to do?</p>
            <p class="rooted-assistant__timestamp">Rooted Assistant · now</p>
          </div>

          <div class="rooted-assistant__choices rooted-assistant__home">
            <button class="rooted-assistant__choice" type="button" data-action="whatsapp">Write to Maria on WhatsApp</button>
            <button class="rooted-assistant__choice" type="button" data-action="discovery">Book a call with Maria</button>
            <a class="rooted-assistant__choice" href="/upcoming-cohorts">Explore the teacher training</a>
            <a class="rooted-assistant__choice" href="/faq">Read common questions</a>
          </div>

          <form class="rooted-assistant__composer">
            <p class="rooted-assistant__message">Write your question here. When you continue, WhatsApp will open with your message ready to send directly to Maria.</p>
            <label for="rooted-assistant-message" class="rooted-assistant__eyebrow">Your message</label>
            <textarea id="rooted-assistant-message" class="rooted-assistant__textarea" maxlength="1000" placeholder="Hi Maria, I’d love to know more about…"></textarea>
            <div class="rooted-assistant__composer-actions">
              <button class="rooted-assistant__back" type="button">← Back</button>
              <button class="rooted-assistant__send" type="submit">Continue in WhatsApp</button>
            </div>
            <p class="rooted-assistant__note">You’ll have a chance to review the message in WhatsApp before sending.</p>
          </form>

          <div class="rooted-assistant__discovery"></div>
        </div>
      </section>

      <button class="rooted-assistant__launcher" type="button" aria-label="Open Rooted Movement assistant" aria-expanded="false">
        <svg class="rooted-assistant__launcher-chat" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.8 4h14.4A2.8 2.8 0 0 1 22 6.8v8.4a2.8 2.8 0 0 1-2.8 2.8H9l-5.7 3 .9-3.8A2.8 2.8 0 0 1 2 14.5V6.8A2.8 2.8 0 0 1 4.8 4Zm1.7 6.7a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Zm5.5 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Zm5.5 0a1.3 1.3 0 1 0 0 2.6 1.3 1.3 0 0 0 0-2.6Z"/></svg>
        <svg class="rooted-assistant__launcher-close" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="m6.4 5 5.6 5.6L17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4-5.6-5.6L6.4 19 5 17.6l5.6-5.6L5 6.4 6.4 5Z"/></svg>
      </button>
    </div>
  `

  const root = shadow.querySelector('.rooted-assistant')
  if (discoveryOnly) root.dataset.view = 'discovery'
  const panel = shadow.querySelector('.rooted-assistant__panel')
  const launcher = shadow.querySelector('.rooted-assistant__launcher')
  const promo = shadow.querySelector('.rooted-assistant__promo')
  const closeButton = shadow.querySelector('.rooted-assistant__header-close')
  const whatsappChoice = shadow.querySelector('[data-action="whatsapp"]')
  const discoveryChoice = shadow.querySelector('[data-action="discovery"]')
  const discoveryContainer = shadow.querySelector('.rooted-assistant__discovery')
  const composer = shadow.querySelector('.rooted-assistant__composer')
  const textarea = shadow.querySelector('.rooted-assistant__textarea')
  const backButton = shadow.querySelector('.rooted-assistant__back')
  const mobilePromoMedia = window.matchMedia('(max-width: 600px)')
  const desktopPromoMedia = window.matchMedia('(min-width: 1025px)')
  let promoFrame = null

  const syncRouteVisibility = () => {
    const path = window.location.pathname
    root.dataset.homepage = String(path === '/')
    const hidden = (!discoveryOnly && path === '/booking') || path.startsWith('/booking/confirmation')
    root.hidden = hidden
    root.dataset.promoHidden = String(path === '/portugal-pilates-teacher-training')
    if (hidden) setOpen(false)
  }

  const mobilePromoHero = () => document.querySelector([
    'main .hero',
    'main .editorial-hero',
    'main .immersive-hero',
    'main .site-hero',
    'main .testimonials-hero',
    'main .bali-manifesto-hero',
    'main .booking-hero-layout',
    'main .success-hero',
  ].join(', '))

  const syncPromoHiddenState = () => {
    const bookingFormVisible = Array.from(document.querySelectorAll('.bali-booking-form')).some(form => {
      const rect = form.getBoundingClientRect()

      return rect.bottom > 0 && rect.top < window.innerHeight
    })

    root.dataset.promoHidden = String(window.location.pathname === '/portugal-pilates-teacher-training' || bookingFormVisible)
  }

  const syncMobilePromoVisibility = () => {
    const waitsForHero = mobilePromoMedia.matches || desktopPromoMedia.matches
    if (!waitsForHero) {
      root.dataset.promoVisible = 'true'
      return
    }

    const hero = mobilePromoHero()
    const heroHasScrolledPast = hero
      ? hero.getBoundingClientRect().bottom <= 0
      : window.scrollY >= window.innerHeight * 0.75

    root.dataset.promoVisible = String(heroHasScrolledPast)
  }

  const syncTrainingJourneyOverlap = () => {
    const journey = window.location.pathname === '/' ? document.querySelector('[data-training-journey]') : null
    const rect = journey?.getBoundingClientRect()
    const overlapsJourney = Boolean(rect && rect.bottom > 0 && rect.top < window.innerHeight)

    root.dataset.journeyOverlap = String(overlapsJourney)
    if (overlapsJourney && [promo, launcher].includes(shadow.activeElement)) shadow.activeElement.blur()
  }

  const syncFooterOverlap = () => {
    const footerContent = document.querySelector('#main-content > div > footer > div')
      || document.querySelector('#main-content .site-footer__inner')

    if (!footerContent) {
      root.dataset.footerOverlap = 'false'
      return
    }

    const footerRect = footerContent.getBoundingClientRect()
    const launcherRect = launcher.getBoundingClientRect()
    const collisionBuffer = 8
    const overlapsLauncher = footerRect.top <= launcherRect.bottom + collisionBuffer
      && footerRect.bottom >= launcherRect.top - collisionBuffer

    root.dataset.footerOverlap = String(overlapsLauncher)
    if (overlapsLauncher && shadow.activeElement === launcher) launcher.blur()
  }

  const requestMobilePromoSync = () => {
    if (promoFrame !== null) return

    promoFrame = window.requestAnimationFrame(() => {
      const homepage = window.location.pathname === '/'
      root.dataset.homepage = String(homepage)
      if (homepage) {
        // On Home, only the training journey suppresses the closed floating controls.
        root.dataset.promoHidden = 'false'
        root.dataset.promoVisible = 'true'
        root.dataset.footerOverlap = 'false'
        syncTrainingJourneyOverlap()
      } else {
        syncPromoHiddenState()
        syncMobilePromoVisibility()
        syncTrainingJourneyOverlap()
        syncFooterOverlap()
      }
      promoFrame = null
    })
  }

  const setOpen = open => {
    root.dataset.open = String(open)
    panel.setAttribute('aria-hidden', String(!open))
    panel.inert = !open
    launcher.setAttribute('aria-expanded', String(open))
    launcher.setAttribute('aria-label', open ? 'Close Rooted Movement assistant' : 'Open Rooted Movement assistant')

    if (open) {
      requestAnimationFrame(() => {
        if (!disposed && root.dataset.open === 'true') panel.focus({ preventScroll: true })
      })
    } else {
      root.dataset.view = discoveryOnly ? 'discovery' : 'home'
    }
  }

  const openAssistant = () => { returnFocusToOrigin = false; setOpen(true) }
  let lastExternalFocus = null
  let returnFocusToOrigin = false

  const restoreAssistantFocus = () => {
    if (!discoveryOnly && !returnFocusToOrigin && root.dataset.journeyOverlap !== 'true') {
      launcher.focus({ preventScroll: true })
      return
    }

    // The journey hides the launcher, not an already-open conversation.
    for (const target of [lastExternalFocus, document.querySelector('#main-content, #portal-main-content')]) {
      if (!target?.isConnected || target.closest('[inert], [hidden]') || !target.getClientRects().length) continue
      const style = window.getComputedStyle(target)
      if (style.visibility !== 'visible' || style.opacity === '0') continue

      target.focus({ preventScroll: true })
      if (target.getRootNode().activeElement === target) return
    }
  }

  listen(document, 'pointerdown', event => {
    if (event.composedPath().includes(host)) return
    const target = event.composedPath()[0]
    const control = target instanceof Element ? target.closest('button, a[href], input, select, textarea, [tabindex]') : null
    if (control) lastExternalFocus = control
  }, { capture: true })

  listen(document, 'focusin', event => {
    if (!event.composedPath().includes(host)) lastExternalFocus = event.target
  })

  listen(window, 'scroll', requestMobilePromoSync, { passive: true })
  listen(window, 'resize', requestMobilePromoSync, { passive: true })
  listen(mobilePromoMedia, 'change', requestMobilePromoSync)
  const removePromoNavigation = router.on('navigate', () => window.requestAnimationFrame(() => {
    if (disposed) return
    syncRouteVisibility()
    requestMobilePromoSync()
  }))
  syncRouteVisibility()
  requestMobilePromoSync()

  listen(launcher, 'click', () => { returnFocusToOrigin = false; setOpen(root.dataset.open !== 'true') })
  listen(promo, 'click', openAssistant)
  listen(closeButton, 'click', () => {
    setOpen(false)
    restoreAssistantFocus()
  })

  listen(document, 'pointerdown', event => {
    if (root.dataset.open !== 'true') return

    const eventPath = event.composedPath()
    if (eventPath.includes(panel) || eventPath.includes(launcher) || eventPath.includes(promo)) return

    const outsideTarget = eventPath[0] instanceof Element ? eventPath[0] : eventPath[0]?.parentElement
    const outsideLabel = outsideTarget?.closest('label')
    const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [contenteditable]:not([contenteditable="false"]), [tabindex]:not([tabindex="-1"])'
    const associatedControl = outsideLabel?.control
    const intendedFocusTarget = associatedControl?.matches(focusableSelector)
      ? associatedControl
      : outsideTarget?.closest(focusableSelector)
    const previousExternalFocus = lastExternalFocus

    setOpen(false)
    window.setTimeout(() => {
      if (disposed) return
      if (!intendedFocusTarget) {
        restoreAssistantFocus()
        return
      }

      const activeElement = document.activeElement
      const activeFocusIsIntentional = activeElement instanceof Element
        && (activeElement.matches(focusableSelector) || activeElement.closest('dialog[open], [role="dialog"][aria-hidden="false"]'))
      const anotherControlTookFocus = activeFocusIsIntentional
        && activeElement !== intendedFocusTarget
        && activeElement !== previousExternalFocus

      if (anotherControlTookFocus) return
      if (!intendedFocusTarget.isConnected || typeof intendedFocusTarget.focus !== 'function') return

      intendedFocusTarget.focus({ preventScroll: true })
      if (intendedFocusTarget.getRootNode().activeElement !== intendedFocusTarget) {
        restoreAssistantFocus()
      }
    }, 0)
  })

  listen(whatsappChoice, 'click', () => {
    if (discoveryOnly) return
    root.dataset.view = 'whatsapp'
    requestAnimationFrame(() => { if (!disposed) textarea.focus() })
  })

  listen(backButton, 'click', () => {
    if (discoveryOnly) return
    root.dataset.view = 'home'
    whatsappChoice.focus()
  })

  const discoveryCall = createAssistantDiscoveryCall({
    container: discoveryContainer,
    onBack: () => {
      if (discoveryOnly || returnFocusToOrigin) {
        setOpen(false)
        restoreAssistantFocus()
      } else {
        root.dataset.view = 'home'
        discoveryChoice.focus()
      }
    },
  })

  const openDiscoveryAssistant = (sourcePage = 'assistant', pointerOrigin = null) => {
    const active = document.activeElement
    if (active && active !== host && active !== document.body) lastExternalFocus = active
    else if (pointerOrigin?.isConnected) lastExternalFocus = pointerOrigin
    returnFocusToOrigin = discoveryOnly || active !== host || Boolean(pointerOrigin?.isConnected)
    root.dataset.view = 'discovery'
    setOpen(true)
    discoveryCall.open({ sourcePage })
  }

  listen(discoveryChoice, 'click', () => openDiscoveryAssistant())

  listen(composer, 'submit', event => {
    event.preventDefault()
    if (discoveryOnly) return
    const message = textarea.value.trim()
    const whatsappHref = message
      ? `https://wa.me/${mariaWhatsAppNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/${mariaWhatsAppNumber}`

    window.open(whatsappHref, '_blank', 'noopener,noreferrer')
  })

  shadow.querySelectorAll('.rooted-assistant__choice[href]').forEach(link => {
    listen(link, 'click', () => setOpen(false))
  })

  listen(document, 'keydown', event => {
    if (event.key === 'Escape' && root.dataset.open === 'true') {
      setOpen(false)
      restoreAssistantFocus()
    }
  })
  // Same conversation the "Got any questions?" promo opens, started from a page
  // control; closing returns focus to that control (Villa Sky rooms, 2026-10-31).
  const openHomeAssistant = (pointerOrigin = null) => {
    if (discoveryOnly) return
    const active = document.activeElement
    if (active && active !== host && active !== document.body) lastExternalFocus = active
    else if (pointerOrigin?.isConnected) lastExternalFocus = pointerOrigin
    returnFocusToOrigin = true
    root.dataset.view = 'home'
    setOpen(true)
  }

  return {
    openDiscovery: openDiscoveryAssistant,
    openHome: openHomeAssistant,
    destroy() {
      disposed = true
      bindings.forEach(([target, name, callback, options]) => target.removeEventListener(name, callback, options))
      removePromoNavigation()
      if (promoFrame !== null) window.cancelAnimationFrame(promoFrame)
      discoveryCall.destroy()
      host.remove()
    },
  }
}

const assistantSurface = path => {
  if (isAuthPath(path) || isVerificationPath(path) || isBookingBreezePreviewPath(path)
    || isPrivatePurchasePath(path) || isCourseCheckoutPath(path)
    || path.startsWith('/booking/confirmation')) return null
  if (isPortalDiscoveryPath(path)) return 'portal'
  if (path === '/booking') return 'booking'
  return isAppPath(path) ? null : 'public'
}

let assistantController = null
let currentAssistantSurface = null
let lastDiscoveryPointer = null
const syncRootedAssistant = (requestDiscovery = false) => {
  const next = assistantSurface(window.location.pathname)
  if (lastDiscoveryPointer?.path !== window.location.pathname || !next) lastDiscoveryPointer = null
  if (next === currentAssistantSurface && assistantController) return assistantController
  assistantController?.destroy()
  assistantController = null
  currentAssistantSurface = next
  if (next && (next === 'public' || requestDiscovery === true)) {
    assistantController = installRootedAssistant({ discoveryOnly: next !== 'public' })
  }
  return assistantController
}

// Keep the first pointer trigger even before a lazy Portal host exists. Some
// browsers do not focus clicked buttons; keyboard focus still takes precedence.
document.addEventListener('pointerdown', event => {
  lastDiscoveryPointer = null
  const path = event.composedPath()
  if (!assistantSurface(window.location.pathname) || path.some(node => node?.id === 'rooted-assistant-shadow-host')) return
  const target = path[0]
  const control = target instanceof Element ? target.closest('button, a[href], input, select, textarea, [tabindex]') : null
  if (control) lastDiscoveryPointer = { control, path: window.location.pathname }
}, { capture: true })

// Resolve the current route before opening: an Inertia page may request a call
// while its navigate callbacks are still pending. Excluded routes stay inert.
const onDiscoveryRequest = event => {
  const controller = syncRootedAssistant(true)
  const pointerOrigin = lastDiscoveryPointer?.control || null
  lastDiscoveryPointer = null
  controller?.openDiscovery(event.detail?.sourcePage, pointerOrigin)
}
window.addEventListener('rooted:assistant:open-discovery', onDiscoveryRequest)

const onAssistantRequest = () => {
  const controller = syncRootedAssistant()
  const pointerOrigin = lastDiscoveryPointer?.control || null
  lastDiscoveryPointer = null
  controller?.openHome(pointerOrigin)
}
window.addEventListener('rooted:assistant:open', onAssistantRequest)

let portalFreshnessCheckInFlight = false

const reloadStaleStandalonePortal = async () => {
  if (!isStandaloneApp() || portalFreshnessCheckInFlight) return

  portalFreshnessCheckInFlight = true

  try {
    const currentSignature = assetEntrySignature(document)
    if (!currentSignature) return

    const response = await fetch(window.location.href, {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: { 'X-Portal-Freshness-Check': '1' },
    })

    if (!response.ok) return

    const html = await response.text()
    const nextDocument = new DOMParser().parseFromString(html, 'text/html')
    const nextSignature = assetEntrySignature(nextDocument)
    const recovery = planAssetRecovery({
      currentSignature,
      nextSignature,
      storage: availableSessionStorage(window),
    })

    if (recovery === 'reload') window.location.reload()
    else if (recovery === 'manual') showAssetRecoveryNotice()
  } catch (error) {
    console.warn('Portal freshness check failed:', error)
  } finally {
    portalFreshnessCheckInFlight = false
  }
}

const initialPage = mountElement ? initialPageFromDocument(document) : null

if (initialPage) {
  // Public marketing pages may arrive server-rendered; hydrate those in place.
  // Everything else (and any SSR fallback) mounts exactly as before.
  const createRootApp = mountElement.hasAttribute('data-server-rendered') ? createSSRApp : createApp

  createInertiaApp({
    page: initialPage,
    resolve: resolvePage,
    setup({ el, App, props, plugin }) {
      createRootApp({ render: () => h(App, props) })
        .use(plugin)
        .mount(el)

      installPrivacyNavigationBoundary(router)
      installAnalytics(router, initialPage)
      installCookieConsent(initialPage)
    },
  })
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncRootedAssistant, { once: true })
} else {
  syncRootedAssistant()
}
router.on('navigate', syncRootedAssistant)
window.addEventListener('popstate', syncRootedAssistant)

if ('serviceWorker' in navigator && window.isSecureContext && isAppPath(window.location.pathname)) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js', { scope: '/' })
      .then(registration => registration.update())
      .catch(error => {
        console.warn('Service worker registration failed:', error)
      })
  })
}

window.addEventListener('pageshow', reloadStaleStandalonePortal)
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') reloadStaleStandalonePortal()
})
